"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import {
  ShieldCheck,
  Database,
  Lock,
  Share2,
  Cookie,
  UserCog,
  Baby,
  RefreshCw,
  Mail,
  ChevronRight,
  ArrowLeft,
} from "lucide-react";

const sections = [
  { id: "introduction", title: "1. Giới thiệu", icon: ShieldCheck },
  { id: "data-collection", title: "2. Dữ liệu chúng tôi thu thập", icon: Database },
  { id: "data-usage", title: "3. Cách chúng tôi sử dụng dữ liệu", icon: UserCog },
  { id: "data-sharing", title: "4. Chia sẻ dữ liệu với bên thứ ba", icon: Share2 },
  { id: "data-security", title: "5. Bảo mật dữ liệu", icon: Lock },
  { id: "cookies", title: "6. Cookies & công nghệ theo dõi", icon: Cookie },
  { id: "user-rights", title: "7. Quyền của người dùng", icon: UserCog },
  { id: "children", title: "8. Dữ liệu của trẻ em", icon: Baby },
  { id: "changes", title: "9. Thay đổi chính sách", icon: RefreshCw },
  { id: "contact", title: "10. Liên hệ", icon: Mail },
];

export default function PrivacyPage() {
  const [activeSection, setActiveSection] = useState<string>("introduction");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -70% 0px" }
    );

    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero section với gradient nhẹ */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-background to-orange-100/40 dark:from-teal-900/20 dark:via-background dark:to-orange-600/10" />
        <div className="relative mx-auto max-w-6xl px-6 py-12 sm:px-8 sm:py-16">
          <Link
            href="/"
            className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Quay lại trang chủ
          </Link>

          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-500/10 ring-1 ring-teal-500/20">
              <ShieldCheck className="h-6 w-6 text-teal-600 dark:text-teal-500" />
            </div>
            <span className="inline-flex items-center rounded-full bg-teal-500/10 px-3 py-1 text-xs font-medium text-teal-700 ring-1 ring-inset ring-teal-500/20 dark:text-teal-400">
              Cam kết bảo mật
            </span>
          </div>

          <h1 className="mt-5 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Chính sách bảo mật
          </h1>
          <p className="mt-4 max-w-2xl text-base text-muted-foreground sm:text-lg">
            JB AI tôn trọng quyền riêng tư của bạn. Chính sách này giải thích cách
            chúng tôi thu thập, sử dụng và bảo vệ thông tin cá nhân của bạn khi sử
            dụng nền tảng học thi thông minh.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Cập nhật lần cuối:{" "}
            <span className="font-medium text-foreground">22/05/2026</span>
          </p>
        </div>
      </div>

      {/* Main content với 2 cột: TOC + Content */}
      <div className="mx-auto max-w-6xl px-6 py-10 sm:px-8 lg:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[260px_1fr]">
          {/* Table of Contents (sticky sidebar) */}
          <aside className="lg:sticky lg:top-8 lg:h-fit">
            <div className="rounded-xl border border-border bg-card p-4 shadow-card">
              <h2 className="mb-3 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Nội dung
              </h2>
              <nav className="space-y-0.5">
                {sections.map(({ id, title, icon: Icon }) => {
                  const isActive = activeSection === id;
                  return (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-all ${
                        isActive
                          ? "bg-teal-500/10 font-medium text-teal-700 dark:text-teal-400"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 shrink-0 ${
                          isActive ? "text-teal-600 dark:text-teal-500" : ""
                        }`}
                      />
                      <span className="truncate">{title}</span>
                      {isActive && (
                        <ChevronRight className="ml-auto h-3.5 w-3.5 text-teal-600 dark:text-teal-500" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Article content */}
          <article className="min-w-0">
            <div className="prose-jb space-y-12">
              <Section id="introduction" title="1. Giới thiệu">
                <p>
                  Chào mừng bạn đến với <strong>JB AI</strong> — nền tảng học thi
                  thông minh được vận hành bởi Job & Banking. Chúng tôi cam kết
                  bảo vệ quyền riêng tư của tất cả người dùng truy cập website{" "}
                  <a
                    href="https://web.jb.com.vn"
                    className="text-teal-600 hover:underline dark:text-teal-400"
                  >
                    web.jb.com.vn
                  </a>{" "}
                  cũng như các dịch vụ liên quan.
                </p>
                <p>
                  Bằng việc sử dụng dịch vụ của JB AI, bạn đồng ý với các điều khoản
                  được nêu trong Chính sách bảo mật này. Nếu bạn không đồng ý, vui
                  lòng ngừng sử dụng dịch vụ.
                </p>
              </Section>

              <Section id="data-collection" title="2. Dữ liệu chúng tôi thu thập">
                <p>
                  Chúng tôi thu thập các loại thông tin sau để cung cấp và cải thiện
                  dịch vụ:
                </p>
                <h3>2.1. Thông tin bạn cung cấp trực tiếp</h3>
                <ul>
                  <li>
                    <strong>Thông tin tài khoản</strong>: tên, địa chỉ email, ảnh
                    đại diện (khi đăng ký qua Google OAuth)
                  </li>
                  <li>
                    <strong>Thông tin học tập</strong>: lộ trình học, đề thi đã làm,
                    kết quả, ghi chú cá nhân
                  </li>
                  <li>
                    <strong>Nội dung tương tác</strong>: tin nhắn chat với trợ lý
                    AI, review, bình luận
                  </li>
                </ul>
                <h3>2.2. Thông tin thu thập tự động</h3>
                <ul>
                  <li>Thiết bị, hệ điều hành, trình duyệt, địa chỉ IP</li>
                  <li>Hành vi sử dụng: trang đã xem, thời gian truy cập, tính năng đã dùng</li>
                  <li>Dữ liệu cookies và công nghệ tương tự</li>
                </ul>
              </Section>

              <Section id="data-usage" title="3. Cách chúng tôi sử dụng dữ liệu">
                <p>Thông tin của bạn được dùng để:</p>
                <ul>
                  <li>Cung cấp, vận hành và duy trì dịch vụ JB AI</li>
                  <li>Cá nhân hóa trải nghiệm học tập (gợi ý lộ trình, đề thi phù hợp)</li>
                  <li>Cải thiện chất lượng nội dung và tính năng AI</li>
                  <li>Gửi thông báo quan trọng về tài khoản và dịch vụ</li>
                  <li>Phòng chống gian lận, lạm dụng và bảo vệ an ninh hệ thống</li>
                  <li>Tuân thủ nghĩa vụ pháp lý theo quy định của pháp luật Việt Nam</li>
                </ul>
              </Section>

              <Section id="data-sharing" title="4. Chia sẻ dữ liệu với bên thứ ba">
                <p>
                  Chúng tôi <strong>không bán</strong> dữ liệu cá nhân của bạn. Dữ
                  liệu chỉ được chia sẻ với:
                </p>
                <ul>
                  <li>
                    <strong>Nhà cung cấp dịch vụ</strong>: Google (xác thực OAuth),
                    Supabase (cơ sở dữ liệu), các nhà cung cấp AI để xử lý yêu cầu
                  </li>
                  <li>
                    <strong>Cơ quan pháp luật</strong>: khi có yêu cầu hợp pháp từ
                    cơ quan có thẩm quyền
                  </li>
                  <li>
                    <strong>Trong trường hợp sáp nhập hoặc chuyển nhượng</strong>:
                    dữ liệu có thể được chuyển giao cùng với việc chuyển giao tổ
                    chức
                  </li>
                </ul>
                <p>
                  Tất cả bên thứ ba đều phải tuân thủ các tiêu chuẩn bảo mật tương
                  đương hoặc cao hơn JB AI.
                </p>
              </Section>

              <Section id="data-security" title="5. Bảo mật dữ liệu">
                <p>
                  Chúng tôi áp dụng các biện pháp bảo mật kỹ thuật và tổ chức để
                  bảo vệ dữ liệu của bạn:
                </p>
                <ul>
                  <li>Mã hóa dữ liệu khi truyền (HTTPS/TLS) và khi lưu trữ</li>
                  <li>Phân quyền truy cập nghiêm ngặt theo nguyên tắc tối thiểu</li>
                  <li>Sao lưu định kỳ và kế hoạch khôi phục sau sự cố</li>
                  <li>Giám sát an ninh liên tục 24/7</li>
                </ul>
                <p>
                  Tuy nhiên, không có hệ thống nào tuyệt đối an toàn. Chúng tôi
                  khuyến nghị bạn giữ kín thông tin đăng nhập và thông báo ngay khi
                  phát hiện hoạt động bất thường.
                </p>
              </Section>

              <Section id="cookies" title="6. Cookies & công nghệ theo dõi">
                <p>JB AI sử dụng cookies cho các mục đích sau:</p>
                <ul>
                  <li>
                    <strong>Cookies cần thiết</strong>: duy trì phiên đăng nhập,
                    bảo mật
                  </li>
                  <li>
                    <strong>Cookies chức năng</strong>: ghi nhớ tùy chọn ngôn ngữ,
                    chế độ giao diện
                  </li>
                  <li>
                    <strong>Cookies phân tích</strong>: thống kê cách người dùng
                    tương tác với website (ẩn danh)
                  </li>
                </ul>
                <p>
                  Bạn có thể quản lý cookies thông qua cài đặt trình duyệt. Lưu ý
                  việc tắt cookies cần thiết có thể ảnh hưởng đến chức năng của
                  dịch vụ.
                </p>
              </Section>

              <Section id="user-rights" title="7. Quyền của người dùng">
                <p>Bạn có các quyền sau đối với dữ liệu cá nhân:</p>
                <ul>
                  <li>
                    <strong>Truy cập</strong>: yêu cầu xem dữ liệu chúng tôi đang
                    lưu giữ
                  </li>
                  <li>
                    <strong>Chỉnh sửa</strong>: cập nhật, sửa thông tin không chính
                    xác
                  </li>
                  <li>
                    <strong>Xóa</strong>: yêu cầu xóa tài khoản và dữ liệu liên
                    quan
                  </li>
                  <li>
                    <strong>Phản đối</strong>: từ chối việc xử lý dữ liệu cho mục
                    đích nhất định
                  </li>
                  <li>
                    <strong>Di chuyển</strong>: nhận bản sao dữ liệu ở định dạng có
                    cấu trúc
                  </li>
                </ul>
                <p>
                  Để thực hiện các quyền trên, vui lòng liên hệ với chúng tôi qua
                  email{" "}
                  <a
                    href="mailto:lienhejb@gmail.com"
                    className="text-teal-600 hover:underline dark:text-teal-400"
                  >
                    lienhejb@gmail.com
                  </a>
                  .
                </p>
              </Section>

              <Section id="children" title="8. Dữ liệu của trẻ em">
                <p>
                  Dịch vụ JB AI không hướng đến trẻ em dưới 13 tuổi. Chúng tôi
                  không cố ý thu thập dữ liệu cá nhân của trẻ em. Nếu phát hiện đã
                  thu thập dữ liệu từ trẻ em dưới 13 tuổi mà không có sự đồng ý của
                  phụ huynh hoặc người giám hộ, chúng tôi sẽ xóa ngay lập tức.
                </p>
              </Section>

              <Section id="changes" title="9. Thay đổi chính sách">
                <p>
                  Chúng tôi có thể cập nhật Chính sách bảo mật này theo thời gian.
                  Các thay đổi quan trọng sẽ được thông báo qua email hoặc thông
                  báo nổi bật trên website ít nhất 7 ngày trước khi có hiệu lực.
                </p>
                <p>
                  Việc tiếp tục sử dụng dịch vụ sau khi chính sách được cập nhật
                  đồng nghĩa với việc bạn chấp nhận các điều khoản mới.
                </p>
              </Section>

              <Section id="contact" title="10. Liên hệ">
                <p>
                  Mọi thắc mắc về Chính sách bảo mật, vui lòng liên hệ:
                </p>
                <div className="not-prose mt-4 rounded-xl border border-border bg-gradient-to-br from-teal-50 to-orange-100/40 p-5 dark:from-teal-900/20 dark:to-orange-600/10">
                  <div className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-5 w-5 shrink-0 text-teal-600 dark:text-teal-500" />
                    <div className="space-y-1 text-sm">
                      <p className="font-semibold text-foreground">Job & Banking — JB AI</p>
                      <p className="text-muted-foreground">
                        Email:{" "}
                        <a
                          href="mailto:lienhejb@gmail.com"
                          className="font-medium text-teal-600 hover:underline dark:text-teal-400"
                        >
                          lienhejb@gmail.com
                        </a>
                      </p>
                      <p className="text-muted-foreground">
                        Website:{" "}
                        <a
                          href="https://web.jb.com.vn"
                          className="font-medium text-teal-600 hover:underline dark:text-teal-400"
                        >
                          web.jb.com.vn
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </Section>
            </div>

            {/* Footer link */}
            <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
              <p className="text-sm text-muted-foreground">
                © 2026 JB AI. Tất cả quyền được bảo lưu.
              </p>
              <Link
                href="/terms"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 hover:underline dark:text-teal-400"
              >
                Xem Điều khoản dịch vụ
                <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-8">
      <h2 className="mb-4 font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] leading-relaxed text-muted-foreground [&_a]:font-medium [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:my-1.5 [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6">
        {children}
      </div>
    </section>
  );
}
